import { useModals } from "@/features/modals/hooks";
import {
  type UserInfo,
  getUserInfo,
  type hfb,
  useProfile,
  useSearchTeam,
} from "@repo/api";
import {
  FormField,
  InlineMessage,
  ListView,
  ListViewItem,
  Option,
  Select,
  Switch,
  Text,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ElementsContainer,
  SectionContainer,
  StatelessLabelField,
  translateHFB,
  useOrderedCustomerMeetingPoints,
  useSettings,
} from "@repo/ui";
import { hasItems, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";

import { type ManageTeamStateActions } from "./ManageTeamReducer";
import { MemberList } from "./MemberList";

interface Props {
  actions: ManageTeamStateActions;
}
export const CreateTeamStep = ({ actions }: Props) => {
  const { data: profile } = useProfile();
  const { market } = useSettings();
  const { data: businessUnits } = useOrderedCustomerMeetingPoints();
  const { pushModal } = useModals();

  const [description, setDescription] = useState<string | undefined>();
  const [members, setMembers] = useState<UserInfo[]>([]);
  const [managerEmailId, setManagerEmailId] = useState<
    string | undefined | null
  >(profile?.mail);
  const [hfbs, setHfbs] = useState<hfb[]>([]);
  const [bu, setBu] = useState<[string, string] | undefined>();
  const [name, setName] = useState<string | undefined>();
  const [defaultTeam, setDefaultTeam] = useState<boolean>(false);
  const defaultBuCode = window.salja?.coworkerprofile?.data?.store?.No;

  const { data: searchResult } = useSearchTeam(
    !isEmpty(hfbs) && bu
      ? {
          hfbs,
          buCodes: [bu[0]],
        }
      : undefined
  );

  // set default bu
  useEffect(() => {
    const defaultBu = businessUnits.find((el) => el[0] === defaultBuCode);

    if (defaultBu) {
      setBu(defaultBu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultBuCode]);

  // set default team name
  useEffect(() => {
    setName(bu && hfbs[0] ? `${bu[1]} ${bu[0]} - ${t(hfbs[0])}` : "");
  }, [bu, hfbs]);

  // set default team
  useEffect(() => {
    if (isEmpty(searchResult) && !isEmpty(hfbs) && bu) {
      setDefaultTeam(true);
    } else {
      setDefaultTeam(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult]);

  // Add my profile by default
  useEffect(() => {
    if (profile && !members.find((u) => u.emailId === profile?.mail)) {
      setMembers([...members, getUserInfo(profile)]);
    }
  }, [profile, members]);

  useEffect(() => {
    actions.setNewTeam({
      teamName: name ?? "",
      teamDescription: description ?? "",
      default: defaultTeam,
      countryCode: market.toUpperCase(),
      managerEmailId: managerEmailId ?? members[0]?.emailId,
      teamMemberEmails: members.map((el) => el.emailId),
      hfbs,
      buCode: bu?.[0],
    });
  }, [
    name,
    description,
    hfbs,
    bu,
    members,
    market,
    managerEmailId,
    defaultTeam,
    actions,
  ]);

  return (
    <SectionContainer>
      <ElementsContainer>
        <FormField
          shouldValidate
          valid={bu !== undefined && bu?.[0] !== t("action.choose_an_option")}
          validation={{
            msg: t("label.required_field"),
            id: "error",
            type: "error",
          }}
        >
          <Select
            id="team-bu-select"
            defaultValue={defaultBuCode}
            label={t("label.business_unit")}
            onChange={(e) => {
              const selectedBu = businessUnits.find(
                (el) => el[0] === e.target.value
              );
              setBu(selectedBu);
              trackAnalytics("business_unit");
            }}
          >
            {businessUnits.map((el) => (
              <Option key={el[0]} value={el[0]} name={el[1]} />
            ))}
          </Select>
        </FormField>
        <FormField>
          <ListView id="manage-team-department">
            <ListViewItem
              control="navigational"
              title={t("label.department")}
              description={
                hasItems(hfbs) ? (
                  hfbs.map((el) => translateHFB(el)).join(", ")
                ) : (
                  <FormField
                    valid={hasItems(hfbs)}
                    shouldValidate
                    validation={{
                      msg: t("label.required_field"),
                      id: "error",
                      type: "error",
                    }}
                  />
                )
              }
              onClick={() => {
                pushModal({
                  type: "SELECT_DEPARTMENT",
                  selected: hfbs ?? ([] as hfb[]),
                  onUpdate: (newHfbs) => {
                    setHfbs(newHfbs);
                  },
                  useBokaConfig: true,
                });
                trackAnalytics("select_team_department");
              }}
            />
          </ListView>
        </FormField>
        {!isEmpty(searchResult) && (
          <InlineMessage
            title={t("prompt.existing_team")}
            body={t("prompt.existing_team_desc")}
            variant="cautionary"
          />
        )}
        <FormField
          fieldHelper={{
            msg: t("prompt.default_team_description"),
          }}
        >
          <Switch
            id="team-default"
            subtle
            label={t("label.default_team")}
            value={defaultTeam.toString()}
            defaultChecked={defaultTeam}
            checked={defaultTeam}
            onChange={(e) => {
              setDefaultTeam(e.target.checked);
            }}
          />
        </FormField>
        <FormField
          valid={(name ?? "").length > 0}
          shouldValidate
          fieldHelper={{
            msg: t("prompt.team_title_helper"),
          }}
          validation={{
            msg: t("label.required_field"),
            id: "error",
            type: "error",
          }}
        >
          <StatelessLabelField
            id="team-name-field"
            label={t("label.name")}
            editable
            value={name}
            onChange={setName}
          />
        </FormField>

        <FormField
          shouldValidate
          valid={(description ?? "").length < 120}
          validation={{
            msg: t("prompt.max_120_characters"),
            id: "error",
            type: "error",
          }}
        >
          <StatelessLabelField
            id="team-description-field"
            label={t("label.description")}
            editable
            value={description}
            onChange={setDescription}
          />
        </FormField>
      </ElementsContainer>
      <Text textStyle="Body.S">{t("label.members")}</Text>
      <MemberList
        key={`create-team-${members.map((m) => m.emailId).join(",")}`}
        members={members}
        managerEmailId={managerEmailId}
        setMembers={setMembers}
        setManager={(userInfo) => {
          setManagerEmailId(userInfo.emailId);
        }}
        editMode
      />
    </SectionContainer>
  );
};
